import { useEffect, useState } from "react";
import { Box, Typography, Divider, Loader, KeyboardArrowRightIcon, IconButton, BookmarkIcon, 
  SVGIcon, LoginPrompt } from "src/components";
import { useAuth, api } from "src/utils";
import { getCategoryById } from "src/data";

export default function BookmarksTab({ handleCategoryClick, handleArticleClick }) {
  const { user, setUser } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);

  const noBookmarks = bookmarks.length === 0 || !user;

  // Fetch bookmarked articles
  useEffect(() => {
    const fetchBookmarks = async () => {
      try {
        if (!user) return;

        const res = await api.get("/user/bookmarks");
        setBookmarks(res.data);
      } catch (err) {
        console.error("Failed to fetch bookmarks", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookmarks();
  }, [user]);

  // Toggle bookmark status (unbookmark)
  const handleBookmarkToggle = async (id) => {
    if (!user) return;

    setLoading(true);

    try {
      const res = await api.post(`/user/bookmarks/${id}`);
      const updatedBookmarks = res.data;

      // Update local user context
      setUser((prev) => ({
        ...prev,
        bookmarks: updatedBookmarks,
      }));

      // Filter articles no longer bookmarked
      setBookmarks((prev) => prev.filter((a) => updatedBookmarks.includes(a.id)));
    } catch (err) {
      console.error("Error toggling bookmark", err);
    } finally {
      setLoading(false);
    }
  };

  // Group bookmarks by category
  const groupedBookmarks = bookmarks.reduce((acc, article) => {
    const category = getCategoryById(article.categoryId);
    if (!category) return acc;

    const key = category.slug;
    if (!acc[key]) {
      acc[key] = { category, articles: [] };
    }

    acc[key].articles.push(article);
    return acc;
  }, {});

  return (
    <>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
        Bookmarks
      </Typography>

      <Divider sx={{ mb: 1 }} />

      {noBookmarks && user && (
        <Box
          sx={{
            p: 2,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 1,
            height: "140px",
            justifyContent: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: "text.secondary" }}>
            Save articles to your bookmarks.
          </Typography>
        </Box>
      )}

      {!user && (
        <LoginPrompt 
          message={"Log in to bookmark your favourite articles."}
          height={"200px"} />
      )}

      {Object.values(groupedBookmarks).map(({ category, articles }) => (
        <Box key={category.slug} sx={{ mt: 3 }}>
          {/* Category Header */}
          <Box
            onClick={() => handleCategoryClick(category.slug)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 2,
              cursor: "pointer",
              mb: 1,
              transition: "background 0.3s",
              "&:hover": {
                opacity: 0.9,
              },
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {category.title}
            </Typography>
            <KeyboardArrowRightIcon fontSize="small" />
          </Box>

          {/* Articles */}
          {articles.map((article) => (
            <Box
              key={article.slug}
              display="flex"
              alignItems="center"
              gap={1}
              mb={1}
              sx={{
                cursor: "pointer",
                border: "1px solid #ccc",
                borderRadius: 1,
                pr: 1,
                transition: "0.2s",
                "&:hover": { opacity: 0.85 },
              }}
              onClick={() => handleArticleClick(category.slug, article.slug)}
            >
              {article.imageUrl ? (
                <Box
                  component="img"
                  src={article.imageUrl}
                  alt={article.title}
                  sx={{
                  width: 50,
                  height: 50,
                  borderTopLeftRadius: 1,
                  borderBottomLeftRadius: 1,
                  objectFit: "cover",
                  flexShrink: 0
                }}
              />
              ) : 
                <SVGIcon
                  icon={category.icon}
                  size={40}
                  sx={{ flexShrink: 0, m: 1 }} />
              }

              <Typography variant="body2" noWrap>
                {article.title}
              </Typography>

              <Box sx={{ flexGrow: 1 }} />

              {/* Unbookmark */}
              <IconButton
                onClick={(e) => {
                  e.stopPropagation(); 
                  handleBookmarkToggle(article.id);
                }}
                size="small"
                sx={{ color: "text.secondary" }}
              >
                <BookmarkIcon fontSize="inherit" />
              </IconButton>
            </Box>
          ))}
        </Box>
      ))}

      {loading && <Loader />}
    </>
  );
}
